import {
  Badge,
  Chip,
  FormControlLabel,
  Paper,
  Switch,
  Tab,
  Tabs,
  Tooltip,
  Menu,
  MenuItem,
  Box,
  IconButton,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import {
  CheckCircle,
  HourglassEmptyRounded,
  MoveToInbox,
  Search,
  FilterList,
  Add,
  Group,
  AccountTreeOutlined,
  SyncAlt,
  AccessTime,
  NotificationsActive,
  NotificationsOff,
  PersonSearchOutlined
} from "@mui/icons-material";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams, useNavigate } from "react-router-dom";
import ForumOutlined from "@mui/icons-material/ForumOutlined";
import { AuthContext } from "../../context/Auth/AuthContext";
import { WhatsAppsContext } from "../../context/WhatsApp/WhatsAppsContext";
import { Can } from "../Can";
import NewTicketModal from "../NewTicketModal";
import TabPanel from "../TabPanel";
import TicketsList from "../TicketsList";
import ChatList from "../InternalChat/ChatList";
import QueueMenuItems from "../QueueMenuItems";
import UserFilterMenuItems from "../UserFilterMenuItems";
import ChannelMenuItems from "../ChannelMenuItems";
import toastError from "../../errors/toastError";
import api from "../../services/api";
import { toast } from "react-toastify";
import ConfirmationModal from "../ConfirmationModal";
import usePrefTela from "../../hooks/usePrefTela";
import openSocket from "../../services/socket-io";

const TicketWrapperStyled = styled(Paper)(({ theme }) => ({
  position: "relative",
  display: "flex",
  height: "100%",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  border: "none",
  backgroundColor: theme.palette.background.default,
}));

const TabPanelStyled = styled(TabPanel)(({ theme }) => ({
  position: "relative",
  display: "flex",
  height: "100%",
  flexDirection: "column",
  overflow: "hidden",
  borderRadius: 0,
  backgroundColor: theme.palette.background.default,
}));

const SearchContainerStyled = styled(Paper)(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  padding: theme.spacing(1.5, 2),
  borderBottom: "none",
  backgroundColor: theme.palette.background.paper,
  position: "relative",
  boxShadow: theme.shadows[1],
  "&::after": {
    content: '""',
    position: "absolute",
    bottom: 0,
    left: theme.spacing(2),
    right: theme.spacing(2),
    height: "1px",
    backgroundColor: theme.palette.divider,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(1),
  },
}));

const SearchIconStyled = styled(Search)(({ theme }) => ({
  color: theme.palette.primary.main,
  marginRight: theme.spacing(1),
  fontSize: "1.2rem",
}));
 
const SearchInputStyled = styled("input")(({ theme }) => ({
  flex: 1,
  border: "none",
  borderRadius: theme.shape.borderRadius,
  padding: theme.spacing(1, 1.5),
  outline: "none",
  backgroundColor: theme.palette.action.hover,
  color: theme.palette.text.primary,
  fontSize: "0.9rem",
  transition: "all 0.2s ease",
  "&:focus": {
    backgroundColor: theme.palette.background.paper,
    boxShadow: `0 0 0 2px ${theme.palette.primary.main}30`,
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8, 1.2),
  },
}));

const TabsHeaderStyled = styled(Paper)(({ theme }) => ({
  flex: "none",
  backgroundColor: theme.palette.background.paper,
  boxShadow: "none",
  borderRadius: 0,
  position: "relative",
  zIndex: 1,
  "& .MuiTabs-indicator": {
    height: 3,
    borderRadius: "3px 3px 0 0",
  },
  "& .MuiTab-root": {
    minHeight: 48,
    transition: "all 0.2s ease",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: theme.palette.action.hover,
    },
  },
}));

const TicketOptionsBoxStyled = styled(Paper)(({ theme }) => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  flexWrap: "wrap",
  backgroundColor: theme.palette.background.paper,
  padding: theme.spacing(1.2, 2),
  boxShadow: theme.shadows[1],
  borderRadius: 0,
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.5, 1),
    justifyContent: "flex-start",
    flexWrap: "nowrap",
    overflowX: "auto",
    overflowY: "hidden",
    gap: theme.spacing(0.5),
    // esconde a barra de rolagem mantendo o scroll por toque
    scrollbarWidth: "none",
    "&::-webkit-scrollbar": { display: "none" },
  },
}));

const ActionButton = styled(IconButton)(({ theme, color = "default" }) => ({
  borderRadius: "8px",
  padding: theme.spacing(1),
  backgroundColor: color === "primary" ? 
    `${theme.palette.primary.main}20` : 
    theme.palette.action.hover,
  color: color === "primary" ? theme.palette.primary.main : theme.palette.text.secondary,
  transition: "all 0.2s ease",
  marginRight: theme.spacing(1),
  "&:hover": {
    backgroundColor: color === "primary" ? 
      `${theme.palette.primary.main}40` : 
      theme.palette.action.selected,
    transform: "translateY(-2px)",
  },
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(0.8),
    marginRight: theme.spacing(0.5),
  },
}));

const StyledBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: theme.palette.secondary.main,
    color: theme.palette.secondary.contrastText,
    fontWeight: "bold",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
  },
}));

const UnreadBadge = styled(Badge)(({ theme }) => ({
  "& .MuiBadge-badge": {
    backgroundColor: "#4caf50",
    color: "#fff",
    fontWeight: "bold",
    fontSize: "0.65rem",
    minWidth: "18px",
    height: "18px",
    borderRadius: "9px",
    boxShadow: `0 0 0 2px ${theme.palette.background.paper}`,
    animation: "pulse-unread 1.5s ease-in-out infinite",
  },
  "@keyframes pulse-unread": {
    "0%": { boxShadow: `0 0 0 0 rgba(76, 175, 80, 0.7)` },
    "70%": { boxShadow: `0 0 0 6px rgba(76, 175, 80, 0)` },
    "100%": { boxShadow: `0 0 0 0 rgba(76, 175, 80, 0)` },
  },
}));

const TicketsManager = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [searchParam, setSearchParam] = useState("");
  const [tab, setTab] = useState("open");
  const [newTicketModalOpen, setNewTicketModalOpen] = useState(false);
  const [showAllTickets, setShowAllTickets] = useState(false);
  // Filtro de janela WABA de 24h: estados selecionados ("open"|"closing"|"closed").
  // Vazio = sem filtro. Só aparece se a instância tem conexão oficial (waba).
  const [windowStates, setWindowStates] = useState([]);
  // Contagem ao vivo por estado da janela (mostrada nos chips). Só conexão oficial.
  const [windowCounts, setWindowCounts] = useState({ open: 0, closing: 0, closed: 0 });
  const [anchorEl, setAnchorEl] = useState(null);
  const [confirmationOpen, setConfirmationOpen] = useState(false);
  const [closeAction, setCloseAction] = useState(null);
  const { user } = useContext(AuthContext);

  // Silenciar grupos (13/10/2026): preferência do usuário salva no servidor.
  // Estado local para o botão responder na hora; o evento "user" do socket
  // atualiza o AuthContext (e o NotificationsPopOver, que decide o som).
  const [muteGroups, setMuteGroups] = useState(Boolean(user?.muteGroups));
  const [savingMuteGroups, setSavingMuteGroups] = useState(false);

  useEffect(() => {
    setMuteGroups(Boolean(user?.muteGroups));
  }, [user?.muteGroups]);

  const handleToggleMuteGroups = async () => {
    const next = !muteGroups;
    setMuteGroups(next);
    setSavingMuteGroups(true);
    try {
      await api.put("/users/me/mute-groups", { muteGroups: next });
      toast.success(
        next
          ? "Grupos silenciados: sem som e sem aviso (o contador continua)."
          : "Grupos com som de novo."
      );
    } catch (err) {
      setMuteGroups(!next);
      toastError(err);
    } finally {
      setSavingMuteGroups(false);
    }
  };
  const { whatsApps } = useContext(WhatsAppsContext);
  const hasWaba = (whatsApps || []).some((w) => w.isOfficialWaba);
  const wabaWindowParam = windowStates.join(",");
  const toggleWindowState = (key) =>
    setWindowStates((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );

  const [openCount, setOpenCount] = useState(0);
  const [pendingCount, setPendingCount] = useState(0);
  const [closedCount, setClosedCount] = useState(0);
  const [internalUnread, setInternalUnread] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [openGroupsCount, setOpenGroupsCount] = useState(0);
  const [unreadOpenCount, setUnreadOpenCount] = useState(0);
  const [unreadGroupsCount, setUnreadGroupsCount] = useState(0);
  const userQueueIds = user?.queues?.map((q) => q.id);
  const [settings, setSettings] = useState([]);
  const [selectedQueueIds, setSelectedQueueIds] = useState([]);
  // Filtro por atendente — só administrador (ver UserFilterMenuItems)
  const [selectedUserIds, setSelectedUserIds] = useState([]);
  const [userFilterAnchorEl, setUserFilterAnchorEl] = useState(null);
  const [selectedChannelIds, setSelectedChannelIds] = useState([]);
  const [filterMenuAnchorEl, setFilterMenuAnchorEl] = useState(null);
  const [channelMenuAnchorEl, setChannelMenuAnchorEl] = useState(null);

  // Busca as contagens por estado da janela para a aba/fila/canal atuais e
  // atualiza a cada 60s (a janela "anda" no tempo). Só roda se houver WABA.
  // ⚠️ Fica DEPOIS de selectedQueueIds/selectedChannelIds — usá-las antes da
  // declaração (no array de deps) causa "Cannot access before initialization".
  useEffect(() => {
    if (!hasWaba) return undefined;
    const statusByTab = {
      open: "open",
      groups: "open",
      pending: "pending",
      closed: "closed"
    };
    const status = statusByTab[tab] || "open";
    const isGroup = tab === "groups";
    const queueIdsToSend =
      selectedQueueIds && selectedQueueIds.length > 0
        ? selectedQueueIds
        : (user?.queues || []).map((q) => q.id);

    let active = true;
    const fetchCounts = async () => {
      try {
        const { data } = await api.get("/tickets/waba-window-counts", {
          params: {
            status,
            isGroup,
            showAll: tab === "pending" ? true : showAllTickets,
            queueIds: JSON.stringify(queueIdsToSend),
            channelIds: JSON.stringify(selectedChannelIds || [])
          }
        });
        if (active && data) setWindowCounts(data);
      } catch (e) {
        // contador é acessório — falha silenciosa não atrapalha a tela
      }
    };
    fetchCounts();
    const id = setInterval(fetchCounts, 60000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [hasWaba, tab, selectedQueueIds, selectedChannelIds, showAllTickets, user]);

  const fetchTicketCounts = async () => {
    try {
      const queueIdsToSend = selectedQueueIds.length > 0
        ? selectedQueueIds
        : user?.queues?.map(q => q.id) || [];
      const { data } = await api.get("/tickets/count", {
        params: {
          queueIds: queueIdsToSend.length > 0 ? queueIdsToSend.join(",") : undefined,
          showAll: showAllTickets
        }
      });
      
      setOpenCount(data.open || 0);
      setPendingCount(data.pending || 0);
      setClosedCount(data.closed || 0);
      setOpenGroupsCount(data.openGroups || 0);
      setUnreadOpenCount(data.unreadOpen || 0);
      setUnreadGroupsCount(data.unreadGroups || 0);
    } catch (err) {
      console.error("Erro ao buscar contadores:", err);
    }
  };

  useEffect(() => {
    // Antes com deps [] : rodava so no mount. O `user` vem do AuthContext de forma
    // ASSINCRONA (refresh_token), entao no primeiro render `profile` era undefined,
    // a condicao falhava e nunca mais era reavaliada — o admin ficava com
    // showAll=false a sessao inteira e a aba de ativos aparecia VAZIA.
    // Deps por id/profile (primitivos) para nao reexecutar a cada re-render do contexto.
    const perfil = user?.profile?.toUpperCase();
    if (perfil === "ADMIN" || perfil === "MASTERADMIN") {
      setShowAllTickets(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.id, user?.profile]);
  
  useEffect(() => {
    const checkTabChange = () => {
      const changeTabRequest = localStorage.getItem("pressticket:changeTab");
      if (changeTabRequest) {
        setTab(changeTabRequest);
        localStorage.removeItem("pressticket:changeTab");
      }
    };
    
    checkTabChange();
    const intervalId = setInterval(checkTabChange, 1000);
    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const checkTabChange = () => {
      const changeTabRequest = localStorage.getItem("pressticket:changeTab");
      if (changeTabRequest && changeTabRequest !== tab) {
        setTab(changeTabRequest);
        localStorage.removeItem("pressticket:changeTab");
      }
    };
    
    checkTabChange();
  }, [tab]);

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const { data } = await api.get("/settings");
        setSettings(data);
      } catch (err) {
        console.error(err);
        toastError(err);
        setSettings([]);
      }
    };
    fetchSettings();
  }, []);

  useEffect(() => {
    fetchTicketCounts();
    
    const socket = openSocket();
    if (!socket) {
      console.error("Não foi possível conectar ao socket para contadores");
      return;
    }

    let debounceTimer = null;
    
    const debouncedFetchCounts = () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      debounceTimer = setTimeout(() => {
        fetchTicketCounts();
      }, 500);
    };

    const handleTicketUpdate = (data) => {
      if (data.action === "updateCounter" && data.counters) {
        setOpenCount(data.counters.open || 0);
        setPendingCount(data.counters.pending || 0);
        setClosedCount(data.counters.closed || 0);
        setOpenGroupsCount(data.counters.openGroups || 0);
        if (data.counters.unreadOpen !== undefined) setUnreadOpenCount(data.counters.unreadOpen);
        if (data.counters.unreadGroups !== undefined) setUnreadGroupsCount(data.counters.unreadGroups);
      } else if (data.action === "update" || data.action === "create" || data.action === "delete") {
        debouncedFetchCounts();
      }
    };

    const handleAppMessage = (data) => {    
      if (data.action === "create") {
        debouncedFetchCounts();
      }
    };

    socket.on("ticket", handleTicketUpdate);
    socket.on("appMessage", handleAppMessage);
    socket.emit("joinTickets", "open");
    socket.emit("joinTickets", "pending");
    socket.emit("joinTickets", "closed");

    return () => {
      if (debounceTimer) {
        clearTimeout(debounceTimer);
      }
      socket.off("ticket", handleTicketUpdate);
      socket.off("appMessage", handleAppMessage);
    };
  }, [selectedQueueIds, showAllTickets]);
  
  // "#58822" + Enter abre o ticket direto (no app instalado não há barra de endereço)
  const handleSearchKeyDown = (e) => {
    if (e.key !== "Enter") return;
    const m = /^#\s*(\d{1,10})$/.exec((searchParam || "").trim());
    if (m) navigate(`/tickets/${m[1]}`);
  };

  const handleSearch = (e) => {
    const searchedTerm = e.target.value.toLowerCase();

    setSearchParam(searchedTerm);
    if (searchedTerm === "") {
      setTab("open");
    } else if (tab !== "search") {
      setTab("search");
    }

  };

  // Contador da aba interna: é o que avisa que chegou mensagem da equipe
  // enquanto o atendente está com a lista de tickets na frente.
  useEffect(() => {
    const fetchUnread = async () => {
      try {
        const { data } = await api.get("/internal-chats/unread-count");
        setInternalUnread(data?.count || 0);
      } catch (err) {
        setInternalUnread(0);
      }
    };
    fetchUnread();

    const socket = openSocket();
    const onChatEvent = () => fetchUnread();
    socket?.on("internalChat", onChatEvent);
    window.addEventListener("internalchat:unread-refresh", onChatEvent);

    return () => {
      socket?.off("internalChat", onChatEvent);
      window.removeEventListener("internalchat:unread-refresh", onChatEvent);
    };
  }, []);

  const handleChangeTab = (e, newValue) => {
    setTab(newValue);
    // Sair da aba interna solta o painel da direita, senão a conversa da equipe
    // ficaria aberta ao lado de uma lista de clientes.
    if (newValue !== "internal" && searchParams.get("interno")) {
      setSearchParams({});
    }
  };

  const handleCloseTickets = async (status) => {
    try {
      let groupsOnlyParam = '';
      const originalStatus = status;
      
      if (status === 'open') {
        groupsOnlyParam = '&groupsOnly=false';
      } else if (status === 'groups') {
        groupsOnlyParam = '&groupsOnly=true';
        status = 'open';
      }
      
      const { data } = await api.put(`/tickets/close-all?status=${status}${groupsOnlyParam}`);
      setAnchorEl(null);
      
      if (originalStatus === "open") {
        setTab(tab => tab === "open" ? "pending" : "open");
        setTimeout(() => setTab("open"), 100);
      } else if (originalStatus === "pending") {
        setTab(tab => tab === "pending" ? "open" : "pending");
        setTimeout(() => setTab("pending"), 100);
      } else if (originalStatus === "groups") {
        setTab(tab => tab === "groups" ? "open" : "groups");
        setTimeout(() => setTab("groups"), 100);
      } else if (originalStatus === "all") {
        setTab(tab => tab === "open" ? "pending" : "open");
        setTimeout(() => setTab("open"), 100);
      }

      toast.success(
        t("tickets.notifications.closed.success") + 
        (data.count ? ` (${data.count} ${t("tickets.notifications.closed.tickets")})` : "")
      );
    } catch (err) {
      console.error(err);
      toast.error(
        err.response?.data?.error || 
        t("tickets.notifications.closed.error")
      );
    }
  };

  // "Atualizar" e "Finalizar" saíram da linha de botões e foram para o menu ⋮
  // da barra (sugestão do Jds 04/11). O menu avisa por este evento.
  useEffect(() => {
    const ouvir = (ev) => {
      const d = (ev && ev.detail) || {};
      if (d.acao === "atualizar") {
        setTab(tab => tab === "open" ? "pending" : "open");
        setTimeout(() => setTab("open"), 100);
      } else if (d.acao === "finalizar" && user?.profile?.toUpperCase() === "ADMIN") {
        handleOpenConfirmation(d.status);
      }
    };
    window.addEventListener("sabezap:tickets", ouvir);
    return () => window.removeEventListener("sabezap:tickets", ouvir);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.profile]);

  // Linha de filtros (Todos, janela 24h, canais, filas) pode ser escondida no
  // menu ⋮. Escondida, a janela 24h volta a "sem filtro" para não filtrar
  // às escondidas.
  const mostrarFiltrosLista = usePrefTela("filtrosLista");
  useEffect(() => {
    if (!mostrarFiltrosLista) setWindowStates((w) => (w.length ? [] : w));
  }, [mostrarFiltrosLista]);

  const handleOpenConfirmation = (status) => {
    setCloseAction(status);
    setConfirmationOpen(true);
    setAnchorEl(null);
  };

  const handleConfirmClose = async () => {
    await handleCloseTickets(closeAction);
    setConfirmationOpen(false);
    setCloseAction(null);
  };

  const canTabsSettings = (ts) => {
    return (
      (settings && settings.some(s => s.key === ts && s.value === "enabled")) ||
      (user && user?.profile === "admin")
    );
  };

  return (
    <TicketWrapperStyled
      elevation={0}
    >
      <NewTicketModal
        modalOpen={newTicketModalOpen}
        onClose={(e) => setNewTicketModalOpen(false)}
      />
      <ConfirmationModal
        title={
          closeAction === "all"
            ? t("ticketsManager.confirmationModal.closeAllTitle")
            : closeAction === "open"
            ? t("ticketsManager.confirmationModal.closeOpenTitle")
            : closeAction === "groups"
            ? t("ticketsManager.confirmationModal.closeGroupsTitle")
            : t("ticketsManager.confirmationModal.closePendingTitle")
        }
        open={confirmationOpen}
        onClose={() => setConfirmationOpen(false)}
        onConfirm={handleConfirmClose}
      >
        {closeAction === "all"
          ? t("ticketsManager.confirmationModal.closeAllMessage")
          : closeAction === "open"
          ? t("ticketsManager.confirmationModal.closeOpenMessage")
          : closeAction === "groups"
          ? t("ticketsManager.confirmationModal.closeGroupsMessage")
          : t("ticketsManager.confirmationModal.closePendingMessage")}
      </ConfirmationModal>
      <SearchContainerStyled elevation={0}>
        <SearchIconStyled />
        <SearchInputStyled
          type="text"
          placeholder={t("tickets.search.placeholder")}
          value={searchParam}
          onChange={handleSearch}
          onKeyDown={handleSearchKeyDown}
        />
        <Tooltip title={t("ticketsManager.buttons.newTicket")} arrow placement="top">
          <ActionButton 
            color="primary"
            onClick={() => setNewTicketModalOpen(true)}
          >
            <Add fontSize="small" />
          </ActionButton>
        </Tooltip>
      </SearchContainerStyled>
      <TabsHeaderStyled elevation={0}>
        <Tabs
          value={tab}
          onChange={handleChangeTab}
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
          allowScrollButtonsMobile
          indicatorColor="primary"
          textColor="primary"
          aria-label="ticket tabs"
        >          
          <Tab
            value={"open"}
            icon={
              <UnreadBadge
                badgeContent={unreadOpenCount}
                overlap="circular"
                max={99}
                invisible={unreadOpenCount === 0}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <StyledBadge
                  badgeContent={openCount}
                  overlap="circular"
                  max={999}
                  color="secondary"
                >
                  <Tooltip title={t("tickets.tabs.open.title")} placement="top" arrow>
                    <MoveToInbox />
                  </Tooltip>
                </StyledBadge>
              </UnreadBadge>
            }
            sx={{ minWidth: 100, width: "auto" }}
          />
          <Tab
            value={"groups"}
            icon={
              <UnreadBadge
                badgeContent={unreadGroupsCount}
                overlap="circular"
                max={99}
                invisible={unreadGroupsCount === 0}
                anchorOrigin={{ vertical: "top", horizontal: "left" }}
              >
                <StyledBadge
                  badgeContent={openGroupsCount}
                  overlap="circular"
                  max={999}
                  color="secondary"
                >
                  <Tooltip
                    title={muteGroups ? `${t("tickets.tabs.groups.title")} (silenciados)` : t("tickets.tabs.groups.title")}
                    placement="top"
                    arrow
                  >
                    <Box component="span" sx={{ position: "relative", display: "inline-flex" }}>
                      <Group />
                      {muteGroups && (
                        <NotificationsOff
                          sx={{
                            position: "absolute",
                            right: -6,
                            bottom: -4,
                            fontSize: "0.85rem",
                            color: "text.secondary",
                            bgcolor: "background.paper",
                            borderRadius: "50%",
                          }}
                        />
                      )}
                    </Box>
                  </Tooltip>
                </StyledBadge>
              </UnreadBadge>
            }
            sx={{ minWidth: 100, width: "auto" }}
          /> 
          {canTabsSettings("tabsPending") && (         
            <Tab
              value={"pending"}
              icon={
                <StyledBadge
                  badgeContent={pendingCount}
                  overlap="circular"
                  max={999}
                  color="secondary"
                >
                  <Tooltip title={t("tickets.tabs.pending.title")} placement="top" arrow>
                    <HourglassEmptyRounded />
                  </Tooltip>
                </StyledBadge>
              }
              sx={{ minWidth: 100, width: "auto" }}
            />
          )}
          {canTabsSettings("tabsClosed") && (
            <Tab
              value={"closed"}
              icon={
                // Sem contador: o total de resolvidos (999+) não ajuda o atendente.
                <Tooltip title={t("tickets.tabs.closed.title")} placement="top" arrow>
                  <CheckCircle />
                </Tooltip>
              }  
              sx={{ minWidth: 100, width: "auto" }}
            />
          )}
          {/* Chat interno da equipe. Fica junto das abas de atendimento, mas com
              ícone e cor próprios — dentro dela o destinatário é um COLEGA, não
              o cliente, e é isso que a sinalização precisa deixar claro. */}
          <Tab
            value={"internal"}
            icon={
              <StyledBadge
                badgeContent={internalUnread}
                overlap="circular"
                max={99}
                color="error"
              >
                <Tooltip title="Chat interno da equipe" placement="top" arrow>
                  <ForumOutlined />
                </Tooltip>
              </StyledBadge>
            }
            sx={{ minWidth: 100, width: "auto", color: "warning.dark" }}
          />
        </Tabs>
      </TabsHeaderStyled>
      <TicketOptionsBoxStyled elevation={0} sx={mostrarFiltrosLista ? undefined : { display: "none" }}>

        <Box sx={{ display: "flex", alignItems: "center", flexWrap: isMobile ? "nowrap" : "wrap", flexShrink: 0, gap: 1 }}>
          {tab === "groups" && (
            <Tooltip
              title={
                muteGroups
                  ? "Grupos silenciados (sem som e sem aviso). Clique para voltar o som."
                  : "Silenciar grupos: mensagens de grupo param de apitar, mas continuam no contador."
              }
              placement="top"
              arrow
            >
              <span>
                <IconButton
                  size="small"
                  onClick={handleToggleMuteGroups}
                  disabled={savingMuteGroups}
                  aria-label={muteGroups ? "Ativar som dos grupos" : "Silenciar grupos"}
                  color={muteGroups ? "default" : "primary"}
                >
                  {muteGroups ? <NotificationsOff fontSize="small" /> : <NotificationsActive fontSize="small" />}
                </IconButton>
              </span>
            </Tooltip>
          )}
          <Can
            role={user?.profile || ""}
            perform="tickets-manager:showall"
            yes={() => (
              <FormControlLabel
                label={isMobile ? "" : t("tickets.buttons.showAll")}
                labelPlacement="start"
                control={
                  <Switch
                    size="small"
                    checked={showAllTickets}
                    onChange={() =>
                      setShowAllTickets((prevState) => !prevState)
                    }
                    name="showAllTickets"
                    color="primary"
                  />
                }
                sx={{ marginRight: 2 }}
              />
            )}
          />
          {hasWaba && (
            <Tooltip
              title="Filtrar por janela de 24h (WhatsApp oficial): aberta, fechando ou fechada. Clique para combinar."
              placement="top"
              arrow
            >
              <Box sx={{ display: "flex", alignItems: "center", gap: 0.5, mr: 1 }}>
                {[
                  { key: "open", label: "Aberta", color: "success", count: windowCounts.open },
                  { key: "closing", label: "Fechando", color: "warning", count: windowCounts.closing },
                  { key: "closed", label: "Fechada", color: "error", count: windowCounts.closed },
                ].map((s) => {
                  const active = windowStates.includes(s.key);
                  return (
                    <Chip
                      key={s.key}
                      icon={<AccessTime sx={{ fontSize: "0.85rem !important" }} />}
                      label={`${s.label} ${s.count}`}
                      size="small"
                      clickable
                      color={active ? s.color : "default"}
                      variant={active ? "filled" : "outlined"}
                      onClick={() => toggleWindowState(s.key)}
                      sx={
                        s.key === "closing" && s.count > 0 && !active
                          ? { fontWeight: "bold", borderColor: "warning.main", color: "warning.main" }
                          : undefined
                      }
                    />
                  );
                })}
              </Box>
            </Tooltip>
          )}
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Tooltip title="Canais" placement="top" arrow>
              <ActionButton
                onClick={(event) => setChannelMenuAnchorEl(event.currentTarget)}
                sx={{
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <SyncAlt fontSize="small" />
              </ActionButton>
            </Tooltip>
            <Menu
              anchorEl={channelMenuAnchorEl}
              keepMounted
              open={Boolean(channelMenuAnchorEl)}
              onClose={() => setChannelMenuAnchorEl(null)}
              PaperProps={{
                elevation: 3,
                sx: {
                  borderRadius: theme.shape.borderRadius,
                  minWidth: "280px",
                  padding: "4px",
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: theme.shadows[3],
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'visible',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: theme.palette.background.paper,
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderTop: `1px solid ${theme.palette.divider}`
                  }
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <ChannelMenuItems
                selectedChannelIds={selectedChannelIds}
                channels={whatsApps}
                onChange={(values) => setSelectedChannelIds(values)}
                onClose={() => setChannelMenuAnchorEl(null)}
              />
            </Menu>

            <Tooltip title={t("tickets.buttons.queues")} placement="top" arrow>
              <ActionButton
                onClick={(event) => setFilterMenuAnchorEl(event.currentTarget)}
                sx={{
                  boxShadow: theme.shadows[2],
                  '&:hover': {
                    boxShadow: theme.shadows[4]
                  }
                }}
              >
                <AccountTreeOutlined fontSize="small" />
              </ActionButton>
            </Tooltip>
            <Menu
              anchorEl={filterMenuAnchorEl}
              keepMounted
              open={Boolean(filterMenuAnchorEl)}
              onClose={() => setFilterMenuAnchorEl(null)}
              PaperProps={{
                elevation: 3,
                sx: {
                  borderRadius: theme.shape.borderRadius,
                  minWidth: "220px",
                  padding: "4px",
                  backgroundColor: theme.palette.background.paper,
                  boxShadow: theme.shadows[3],
                  border: `1px solid ${theme.palette.divider}`,
                  overflow: 'visible',
                  '&:before': {
                    content: '""',
                    display: 'block',
                    position: 'absolute',
                    top: 0,
                    right: 14,
                    width: 10,
                    height: 10,
                    bgcolor: theme.palette.background.paper,
                    transform: 'translateY(-50%) rotate(45deg)',
                    zIndex: 0,
                    borderLeft: `1px solid ${theme.palette.divider}`,
                    borderTop: `1px solid ${theme.palette.divider}`
                  }
                },
              }}
              transformOrigin={{ horizontal: 'right', vertical: 'top' }}
              anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
              <QueueMenuItems
                selectedQueueIds={selectedQueueIds}
                userQueues={user?.queues}
                onChange={(values) => setSelectedQueueIds(values)}
                onClose={() => setFilterMenuAnchorEl(null)}
              />
            </Menu>
            {(user?.profile === "admin" || user?.profile === "masteradmin") && (
              <>
                <Tooltip title="Filtrar por atendente" placement="top" arrow>
                  <Badge
                    color="primary"
                    badgeContent={selectedUserIds.length}
                    invisible={selectedUserIds.length === 0}
                  >
                    <ActionButton
                      onClick={(event) => setUserFilterAnchorEl(event.currentTarget)}
                      sx={{
                        boxShadow: theme.shadows[2],
                        '&:hover': { boxShadow: theme.shadows[4] }
                      }}
                    >
                      <PersonSearchOutlined fontSize="small" />
                    </ActionButton>
                  </Badge>
                </Tooltip>
                <Menu
                  anchorEl={userFilterAnchorEl}
                  keepMounted
                  open={Boolean(userFilterAnchorEl)}
                  onClose={() => setUserFilterAnchorEl(null)}
                  PaperProps={{ elevation: 3, sx: { minWidth: "240px" } }}
                  transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                  anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
                >
                  <UserFilterMenuItems
                    selectedUserIds={selectedUserIds}
                    onChange={setSelectedUserIds}
                  />
                </Menu>
              </>
            )}
          </Box>
        </Box>
      </TicketOptionsBoxStyled>
      <TabPanelStyled value={tab} name="open">
        <TicketsList
          status="open"
          showAll={showAllTickets || selectedUserIds.length > 0}
          selectedUserIds={selectedUserIds}
          selectedQueueIds={selectedQueueIds}
          selectedChannelIds={selectedChannelIds}
          updateCount={(val) => setOpenCount(val)}
          isGroup={false}
          wabaWindow={wabaWindowParam}
        />
      </TabPanelStyled>
      <TabPanelStyled value={tab} name="groups">
        <TicketsList
          status="open"
          showAll={showAllTickets}
          selectedQueueIds={selectedQueueIds}
          selectedChannelIds={selectedChannelIds}
          updateCount={(val) => setOpenGroupsCount(val)}
          isGroup={true}
          wabaWindow={wabaWindowParam}
        />
      </TabPanelStyled>
      <TabPanelStyled value={tab} name="pending">
        <TicketsList
          status="pending"
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          selectedChannelIds={selectedChannelIds}
          updateCount={(val) => setPendingCount(val)}
          wabaWindow={wabaWindowParam}
        />
      </TabPanelStyled>
      <TabPanelStyled value={tab} name="closed">
        <TicketsList
          status="closed"
          showAll={showAllTickets || selectedUserIds.length > 0}
          selectedUserIds={selectedUserIds}
          selectedQueueIds={selectedQueueIds}
          selectedChannelIds={selectedChannelIds}
          updateCount={(val) => setClosedCount(val)}
          wabaWindow={wabaWindowParam}
        />
      </TabPanelStyled>
      <TabPanelStyled value={tab} name="search">
        <TicketsList
          searchParam={searchParam}
          showAll={true}
          selectedQueueIds={selectedQueueIds}
          selectedChannelIds={selectedChannelIds}
          wabaWindow={wabaWindowParam}
        />
      </TabPanelStyled>
      <TabPanelStyled value={tab} name="internal">
        <ChatList
          embedded
          selectedChatId={searchParams.get("interno")}
          onSelect={chatId => {
            // A conversa interna ocupa o painel da direita, no lugar do ticket.
            setSearchParams({ interno: String(chatId) });
          }}
        />
      </TabPanelStyled>
    </TicketWrapperStyled>
  );
};

export default TicketsManager;